import React, { useMemo, useState } from "react";
import { X, ArrowDownRight, ArrowUpRight, ArrowLeftRight, Pencil, Search, Check } from "lucide-react";
import { formatInt } from "../../lib/timeMath";

function formatDateIt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTimeIt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Italian full month names → 0-based month index. Case-insensitive lookup via
// `.toLowerCase()`. Kept intentionally minimal (full names only) to avoid
// ambiguity with tokens like "mar" (marzo/martedì/…) as per product spec.
const MONTHS_IT = {
  gennaio: 0,
  febbraio: 1,
  marzo: 2,
  aprile: 3,
  maggio: 4,
  giugno: 5,
  luglio: 6,
  agosto: 7,
  settembre: 8,
  ottobre: 9,
  novembre: 10,
  dicembre: 11,
};

/**
 * Smart parser: interpreta la stringa dell'utente e la separa in due filtri.
 *  - Se il primo token riconoscibile è un mese italiano intero → estrae il mese
 *    e restituisce il resto come causale.
 *  - Altrimenti tutto il testo resta causale (comportamento originario).
 *  Case-insensitive, whitespace-normalizzato.
 */
function parseQuery(q) {
  const trimmed = (q || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!trimmed) return { month: null, causale: "" };
  const tokens = trimmed.split(" ");
  let monthIdx = null;
  const remaining = [];
  for (const t of tokens) {
    if (monthIdx === null && Object.prototype.hasOwnProperty.call(MONTHS_IT, t)) {
      monthIdx = MONTHS_IT[t];
    } else {
      remaining.push(t);
    }
  }
  return { month: monthIdx, causale: remaining.join(" ") };
}

/**
 * Popup con lista completa dei movimenti economici.
 * Raggruppati per giorno (ordine decrescente).
 * Ogni riga: tipo, importo, fonte, ora, nota, tasto elimina.
 */
export default function HistoryDialog({ open, onClose, transactions, accounts, onEditNote }) {
  const [query, setQuery] = useState("");
  // Inline edit state per la modifica ESCLUSIVAMENTE della causale.
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  // Smart filter: month + causale. Reuses the same input.
  //  - Only causale (no month recognized) → preserves original behavior:
  //    case-insensitive partial match on `note`.
  //  - Only month → all transactions in that month (any year, any causale).
  //  - Month + causale → intersection of both.
  const parsed = useMemo(() => parseQuery(query), [query]);
  const hasQuery = query.trim().length > 0;
  const filtered = useMemo(() => {
    if (!hasQuery) return transactions;
    return transactions.filter((tx) => {
      if (parsed.month !== null) {
        const d = new Date(tx.date);
        if (Number.isNaN(d.getTime())) return false;
        if (d.getMonth() !== parsed.month) return false;
      }
      if (parsed.causale) {
        if (!(tx.note || "").toLowerCase().includes(parsed.causale)) return false;
      }
      return true;
    });
  }, [transactions, parsed, hasQuery]);

  if (!open) return null;

  const accountLabelById = Object.fromEntries(accounts.map((a) => [a.id, a.label]));

  // Sort desc by date, group by YYYY-MM-DD
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const groups = sorted.reduce((acc, tx) => {
    const key = (tx.date || "").slice(0, 10);
    (acc[key] = acc[key] || []).push(tx);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups);

  // Net total of the filtered rows — reuses EXACTLY the same array that the
  // filter produced (single source of truth). Contabilità: `in` → +amount,
  // `out` → −amount, `switch` → 0 (transfer between accounts, doesn't change
  // net worth — same semantics used by entrateTotali/speseTotali).
  const filteredNet = filtered.reduce((sum, tx) => {
    const amt = Number(tx.amount) || 0;
    if (tx.type === "in") return sum + amt;
    if (tx.type === "out") return sum - amt;
    return sum; // switch → neutral
  }, 0);
  const filteredCount = filtered.length;
  // Build summary label smartly from the parsed query: month name (upper) +
  // causale text (upper) joined by a space, so it reflects exactly what the
  // filter used (e.g. "AGOSTO", "SPESA", "AGOSTO SPESA").
  const monthName =
    parsed.month !== null
      ? Object.keys(MONTHS_IT).find((k) => MONTHS_IT[k] === parsed.month) || ""
      : "";
  const filteredLabel = [monthName, parsed.causale]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
  const showSummary = hasQuery && filteredCount > 0;

  return (
    <div
      data-testid="history-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-zinc-950 border border-white/10 shadow-[0_0_60px_rgba(16,185,129,0.08)]">
        <div className="flex items-start justify-between p-6 sm:p-8 pb-4 border-b border-white/5">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-400/80 font-mono mb-2">
              TIME Watch · Storico
            </div>
            <h3 className="text-xl sm:text-2xl font-light tracking-tight text-white leading-tight">
              Movimenti economici
            </h3>
            <p className="mt-1 text-[11px] text-zinc-500">
              {transactions.length} {transactions.length === 1 ? "movimento" : "movimenti"}
            </p>
          </div>
          <button
            data-testid="history-close"
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div
          data-testid="history-list"
          className="flex-1 overflow-y-auto px-6 sm:px-8 py-4"
        >
          {/* Free-text search on causale (note). Client-side filter over the
              transactions already loaded from useTimeWatchData. */}
          <div className="relative mb-4">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none"
              aria-hidden="true"
            />
            <input
              data-testid="history-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtra per mese o causale…"
              aria-label="Filtra per mese o causale"
              className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder-zinc-500 focus:border-emerald-400/40 focus:outline-none font-light"
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="history-search-clear"
                aria-label="Cancella filtro"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {groupKeys.length === 0 ? (
            hasQuery ? (
              <p
                data-testid="history-empty-filtered"
                className="text-sm text-zinc-500 text-center py-14 leading-relaxed"
              >
                Nessun movimento trovato per questa ricerca.
              </p>
            ) : (
              <p
                data-testid="history-empty"
                className="text-sm text-zinc-500 text-center py-14 leading-relaxed"
              >
                Nessun movimento registrato.
                <br />
                Aggiungi un&apos;entrata o una spesa dalla pagina Patrimonio.
              </p>
            )
          ) : (
            <div className="space-y-6">
              {groupKeys.map((day) => (
                <div key={day}>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-mono mb-2 sticky top-0 bg-zinc-950 py-1 z-10">
                    {formatDateIt(day)}
                  </div>
                  <ul className="space-y-2">
                    {groups[day].map((tx) => {
                      const isSwitch = tx.type === "switch";
                      const isIn = tx.type === "in";
                      const iconBg = isSwitch
                        ? "bg-sky-400/10 border border-sky-400/25"
                        : isIn
                        ? "bg-emerald-400/10 border border-emerald-400/25"
                        : "bg-red-400/10 border border-red-400/25";
                      const amountColor = isSwitch
                        ? "text-sky-300"
                        : isIn
                        ? "text-emerald-300"
                        : "text-red-300";
                      const amountSign = isSwitch ? "" : isIn ? "+" : "−";
                      return (
                        <li
                          key={tx.id}
                          data-testid={`history-row-${tx.id}`}
                          className="group flex items-center gap-3 py-3 px-3 rounded-lg bg-black/30 border border-white/5 hover:border-white/10 transition-colors"
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}
                          >
                            {isSwitch ? (
                              <ArrowLeftRight className="w-3.5 h-3.5 text-sky-300" />
                            ) : isIn ? (
                              <ArrowDownRight className="w-3.5 h-3.5 text-emerald-300" />
                            ) : (
                              <ArrowUpRight className="w-3.5 h-3.5 text-red-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span
                                className={`text-sm font-mono tabular-nums ${amountColor}`}
                              >
                                {amountSign}€ {formatInt(tx.amount)}
                              </span>
                              <span className="text-[10px] text-zinc-500 font-mono shrink-0">
                                {formatTimeIt(tx.date)}
                              </span>
                            </div>
                            <div className="text-[11px] text-zinc-400 truncate">
                              {isSwitch ? (
                                <>
                                  {accountLabelById[tx.fromAccountId] || "—"}
                                  <span className="text-zinc-600 mx-1">→</span>
                                  {accountLabelById[tx.toAccountId] || "—"}
                                </>
                              ) : (
                                accountLabelById[tx.accountId] || "—"
                              )}
                              {editingId === tx.id ? (
                                <span className="inline-flex items-center gap-1 ml-1 align-middle">
                                  <span className="text-zinc-600">·</span>
                                  <input
                                    data-testid={`history-edit-input-${tx.id}`}
                                    type="text"
                                    value={editingValue}
                                    autoFocus
                                    maxLength={120}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") {
                                        setEditingId(null);
                                        setEditingValue("");
                                      } else if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (typeof onEditNote === "function") {
                                          onEditNote(tx.id, editingValue);
                                        }
                                        setEditingId(null);
                                        setEditingValue("");
                                      }
                                    }}
                                    placeholder="Causale…"
                                    className="bg-zinc-800/80 border border-emerald-400/25 rounded px-1.5 py-0.5 text-[11px] text-zinc-200 focus:outline-none focus:border-emerald-400/60 w-40"
                                  />
                                </span>
                              ) : tx.note ? (
                                <span className="text-zinc-600"> · {tx.note}</span>
                              ) : null}
                            </div>
                          </div>
                          {editingId === tx.id ? (
                            <button
                              data-testid={`history-edit-save-${tx.id}`}
                              type="button"
                              onClick={() => {
                                if (typeof onEditNote === "function") {
                                  onEditNote(tx.id, editingValue);
                                }
                                setEditingId(null);
                                setEditingValue("");
                              }}
                              className="min-w-[44px] min-h-[44px] w-11 h-11 sm:w-8 sm:h-8 sm:min-w-0 sm:min-h-0 rounded-full flex items-center justify-center text-emerald-300 hover:bg-emerald-500/10 active:bg-emerald-500/20 transition-all"
                              style={{
                                touchAction: "manipulation",
                                WebkitTapHighlightColor: "transparent",
                              }}
                              aria-label="Salva causale"
                              title="Salva causale"
                            >
                              <Check className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            </button>
                          ) : (
                            <button
                              data-testid={`history-edit-${tx.id}`}
                              type="button"
                              onClick={() => {
                                setEditingId(tx.id);
                                setEditingValue(tx.note || "");
                              }}
                              className="opacity-70 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 min-w-[44px] min-h-[44px] w-11 h-11 sm:w-8 sm:h-8 sm:min-w-0 sm:min-h-0 rounded-full flex items-center justify-center text-zinc-400 sm:text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10 active:bg-emerald-500/20 transition-all"
                              style={{
                                touchAction: "manipulation",
                                WebkitTapHighlightColor: "transparent",
                              }}
                              aria-label="Modifica causale"
                              title="Modifica causale"
                            >
                              <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}

              {showSummary && (
                <div
                  data-testid="history-filter-summary"
                  className="pt-4 mt-2 border-t border-white/5 flex items-baseline justify-between gap-3"
                >
                  <div className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-mono">
                    <span data-testid="history-filter-count">
                      {filteredCount === 1
                        ? "1 movimento"
                        : `${filteredCount} movimenti`}
                    </span>
                    <span className="text-zinc-600"> · </span>
                    <span data-testid="history-filter-label">
                      Totale {filteredLabel}
                    </span>
                  </div>
                  <div
                    data-testid="history-filter-total"
                    className={`text-base font-mono tabular-nums ${
                      filteredNet > 0
                        ? "text-emerald-300"
                        : filteredNet < 0
                        ? "text-red-300"
                        : "text-zinc-300"
                    }`}
                  >
                    {filteredNet > 0
                      ? "+"
                      : filteredNet < 0
                      ? "−"
                      : ""}
                    € {formatInt(Math.abs(filteredNet))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
